import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Chip, Stack, TableCell, TableRow, Typography, useTheme } from "@mui/material"
import { getInputFiles, InputFile, MultilangJob } from "src/services/api/document"
import { CardColor } from "src/components/browser/card/BrowserCard"
import { ViewLinkButton, languagesRowLabelColor } from "./ViewLinkButton"
import { CsvDialog } from "./CsvDialog"

interface LanguagesRowProps {
  color: CardColor
  multilangJob: MultilangJob
}

export const LanguagesRow = ({ color, multilangJob }: LanguagesRowProps) => {
  const { t: tLang } = useTranslation("lang")
  const { t } = useTranslation("multilang")
  const [openDialog, setOpenDialog] = useState(false)
  const [inputFiles, setInputFiles] = useState<InputFile[]>([])

  const sourceLanguage = multilangJob.langFrom
  const targetLanguages = multilangJob.singleLangJobs.map((j) => j.targetLanguage)

  const handleViewFileList = () => {
    getInputFiles(multilangJob.id)
      .then((files) => {
        setInputFiles(files)
        setOpenDialog(true)
      })
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
  }

  const inputFilesCsvContent = ["File name", ...inputFiles.map((f) => f.name)].join("\n")

  return (
    <>
      <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
        <TableCell sx={{ verticalAlign: "top" }}>
          <Stack spacing={1}>
            <Typography>{t("languages")}</Typography>
            <ViewLinkButton onClick={handleViewFileList} multilangJob={multilangJob}>View file list</ViewLinkButton>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack spacing={1} alignItems={"flex-start"}>
            <LanguagesRowLabel label="SOURCE" multilangJob={multilangJob} />
            <Typography variant="h6">{tLang(sourceLanguage.toLowerCase())}</Typography>
            <LanguagesRowLabel label="TARGET" multilangJob={multilangJob} />
            <Typography variant="h6">{targetLanguages.map((l) => tLang(l.toLowerCase())).join(", ")}</Typography>
          </Stack>
        </TableCell>
      </TableRow>

      <CsvDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title="All added files"
        csvContent={inputFilesCsvContent}
        downloadFilename="input-files.csv"
        titleExtra={
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
            <LanguagesRowLabel label="SOURCE" multilangJob={multilangJob} />
            <Typography variant="body1" sx={{ font: "normal normal 600 14px/17px Inter", letterSpacing: "0px" }}>
              {tLang(sourceLanguage.toLowerCase())}
            </Typography>
          </Stack>
        }
      />
    </>
  )
}

export const LanguagesRowLabel = ({ label, multilangJob }: { label: string; multilangJob: MultilangJob }) => {
  const { palette } = useTheme()
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: languagesRowLabelColor(multilangJob, palette),
        fontSize: "10px",
        lineHeight: "12px",
        fontWeight: 600,
        height: "18px",
        "& .MuiChip-label": {
          px: 0.5
        }
      }}
    />
  )
}
